/**
 * @fileoverview Página responsável por processar convite de canal interno (auto-join + signup público).
 */

import React, { useEffect, useState, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { Hash, Building2 } from "lucide-react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { TERMS_URL, PRIVACY_URL } from "../../components/AuthLayout/TermsCheckbox";
import { i18n } from "../../translate/i18n";
import { makeStyles } from "@material-ui/core/styles";

// A regra global dos links é verde e com !important: aqui só uma classe vence.
const useStyles = makeStyles({
  legalLink: { color: "#E4E4E7 !important", textDecoration: "underline" },
});

const InternalChatInvite = () => {
  const classes = useStyles();
  const { token } = useParams();
  const history = useHistory();
  const { user, isAuth, loading } = useContext(AuthContext);
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [form, setForm] = useState({ name: "", email: "", password: "", acceptedTerms: false });
  const [submitting, setSubmitting] = useState(false);
  const [autoJoining, setAutoJoining] = useState(false);

  // Fetch info do canal (público)
  useEffect(() => {
    api.get(`/internal-chat/public/invite/${token}/info`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ error: true }));
  }, [token]);

  // Se já logado, auto-join
  useEffect(() => {
    if (loading || !info || info.error || !isAuth || !user?.id) return;
    setAutoJoining(true);
    api.post(`/internal-chat/invite/${token}/join`)
      .then(({ data }) => {
        setTimeout(() => history.replace(`/internal-chat/${data.channelId}`), 400);
      })
      .catch(() => { toastError("Convite invalido"); history.replace("/internal-chat"); });
  }, [loading, isAuth, user, info, token, history]);

  const handleSubmitSignup = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toastError("Preencha nome, email e senha (mín 6 caracteres)");
      return;
    }
    if (!form.acceptedTerms) {
      toastError(i18n.t("backendErrors.ERR_TERMS_NOT_ACCEPTED"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/internal-chat/public/invite/${token}/signup`, form);
      // Salvar token + redirecionar
      try {
        localStorage.setItem("token", data.token);
        localStorage.setItem("userId", String(data.user.id));
        localStorage.setItem("companyId", String(data.user.companyId));
      } catch {}
      window.location.href = `/internal-chat/${data.channelId}`;
    } catch (err) {
      toastError(err);
      setSubmitting(false);
    }
  };

  if (loading || !info) {
    return (
      <div style={{ minHeight: "100vh", background: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress style={{ color: "#5B7CFF" }} />
      </div>
    );
  }

  if (info.error) {
    return (
      <div style={{ minHeight: "100vh", background: "#0A0A0A", color: "#FAFAFA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Convite inválido</div>
        <div style={{ fontSize: 14, color: "#A1A1AA" }}>Este link pode ter sido invalidado.</div>
        <button onClick={() => history.replace("/login")} style={{ marginTop: 12, padding: "10px 24px", background: "#5B7CFF", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600 }}>
          Ir para login
        </button>
      </div>
    );
  }

  if (autoJoining) {
    return (
      <div style={{ minHeight: "100vh", background: "#0A0A0A", color: "#FAFAFA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
        <CircularProgress style={{ color: "#5B7CFF" }} />
        <div style={{ fontSize: 14, color: "#A1A1AA" }}>Entrando no canal #{info.channelName}...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", color: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 440, background: "#18181B", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: 14, background: "rgba(91,124,255,0.18)", marginBottom: 12 }}>
            <Hash size={28} color="#5B7CFF" strokeWidth={2.5} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
            Você foi convidado pro canal
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#5B7CFF" }}>#{info.channelName}</div>
          {info.companyName && (
            <div style={{ fontSize: 13, color: "#A1A1AA", marginTop: 6 }}>
              de {info.groupName || info.companyName}
            </div>
          )}
          <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(91,124,255,0.08)", border: "1px solid rgba(91,124,255,0.2)", borderRadius: 10, fontSize: 12, color: "#A1A1AA", textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}>
            <Building2 size={20} color="#5B7CFF" strokeWidth={2} style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, color: "#FAFAFA", fontSize: 12 }}>Você será cadastrado em:</div>
              <div style={{ fontSize: 13, color: "#5B7CFF", fontWeight: 600 }}>{info.companyName}</div>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", padding: 4, borderRadius: 10, marginBottom: 18 }}>
          <button onClick={() => setMode("login")} style={{ flex: 1, padding: "8px 12px", border: "none", borderRadius: 8, background: mode === "login" ? "#5B7CFF" : "transparent", color: mode === "login" ? "#fff" : "#A1A1AA", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Já tenho conta
          </button>
          <button onClick={() => setMode("signup")} style={{ flex: 1, padding: "8px 12px", border: "none", borderRadius: 8, background: mode === "signup" ? "#5B7CFF" : "transparent", color: mode === "signup" ? "#fff" : "#A1A1AA", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Criar conta
          </button>
        </div>

        {mode === "login" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "#A1A1AA", marginBottom: 14 }}>
              Faça login com sua conta. Você será adicionado automaticamente no canal.
            </div>
            <button
              onClick={() => {
                try { localStorage.setItem("postLoginRedirect", `/internal-chat/invite/${token}`); } catch {}
                history.push("/login");
              }}
              style={{ width: "100%", padding: "12px 16px", background: "#5B7CFF", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
            >
              Ir para login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmitSignup} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#A1A1AA", marginBottom: 6 }}>Nome completo</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Maria Silva"
                required
                style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, color: "#FAFAFA", fontSize: 14, outline: "none" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#A1A1AA", marginBottom: 6 }}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="maria@empresa.com"
                required
                style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, color: "#FAFAFA", fontSize: 14, outline: "none" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#A1A1AA", marginBottom: 6 }}>Senha (mín 6 caracteres)</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength={6}
                required
                style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, color: "#FAFAFA", fontSize: 14, outline: "none" }}
              />
            </div>
            <label htmlFor="channel-invite-terms" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.45, color: "#A1A1AA", cursor: "pointer" }}>
              <input
                id="channel-invite-terms"
                type="checkbox"
                checked={form.acceptedTerms}
                onChange={(e) => setForm({ ...form, acceptedTerms: e.target.checked })}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <span>
                {i18n.t("auth.signup.legalPrefix")}{" "}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={classes.legalLink}>{i18n.t("auth.signup.legalTerms")}</a>{" "}
                {i18n.t("auth.signup.legalAnd")}{" "}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={classes.legalLink}>{i18n.t("auth.signup.legalPrivacy")}</a>.
              </span>
            </label>
            <button type="submit" disabled={submitting} style={{ marginTop: 6, padding: "12px 16px", background: submitting ? "rgba(91,124,255,0.5)" : "#5B7CFF", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: submitting ? "default" : "pointer" }}>
              {submitting ? "Cadastrando..." : "Cadastrar e entrar no canal"}
            </button>
            <div style={{ fontSize: 11, color: "#71717A", textAlign: "center", marginTop: 4 }}>
              Sua conta terá acesso apenas ao Chat Interno (sem atendimento).
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InternalChatInvite;
